import React from 'react';
import { useOutletContext } from 'react-router-dom';
import { RestaurantGuardOutletContext } from '../../routes/RestaurantGuard';
import { TableGuardOutletContext } from '../../routes/TableGuard';
import { BookNowButton } from '../buttons/BookNowButton';
import { PatioInfoSection } from '../callouts/PatioInfoSection';
import { useIsMutating } from '@tanstack/react-query';
import { MUTATION_KEY } from '@/api/releaseCheckoutSession';
import { getBookingType, requiresRequestFlow, TDR } from 'tdr-common';
import { useTranslation } from 'react-i18next';

export interface BookNowFooterProps {
	priceAmountTotaled?: number;
	displayUnit: string;
	disabled?: boolean;
	tableHasAnnouncement?: boolean;
	announcementType?: string;
	/**
	 * Resolved for this search's date and groupSize by the availability service. Unset until both
	 * are known -- see the fallback below.
	 */
	bookingType?: TDR.BookingType;
}

export const BookNowFooter = ({
	displayUnit,
	disabled = false,
	priceAmountTotaled,
	tableHasAnnouncement = false,
	announcementType = '',
	bookingType
}: BookNowFooterProps) => {
	const { t } = useTranslation();
	const { table: selectedTable } = useOutletContext<TableGuardOutletContext>();
	const { restaurant, featureFlags } = useOutletContext<RestaurantGuardOutletContext>();
	const hidePricing = !!featureFlags?.hideFreePricing;

	const { placement, symbol } = TDR.CurrencyConfig[restaurant?.currency || TDR.Currency.USD];

	const isMutating = useIsMutating({
		mutationKey: [MUTATION_KEY],
		exact: true
	});

	const CTAOverride = selectedTable?.CTAOverride;

	const displayPrice = () => {
		if (typeof priceAmountTotaled !== 'number') {
			return '';
		}
		return placement === TDR.CurrencyPlacement.BEFORE
			? ` • ${symbol} ${priceAmountTotaled}`
			: ` • ${priceAmountTotaled} ${symbol}`;
	};

	const shouldShowPrice =
		!hidePricing &&
		typeof priceAmountTotaled === 'number' &&
		priceAmountTotaled > 0;

	const tableLabel = selectedTable.label || t('labels.table', { defaultValue: 'table' });

	// The search only resolves a booking type once it knows both date and groupSize. Without them
	// BookNowButton is disabled anyway (it requires date, time AND groupSize), so this label sits on
	// an inert button and falling back to the table's own default is exactly what rendered here
	// before. Whenever the button is actually live, the resolved value is present and wins.
	const effectiveBookingType = bookingType ?? getBookingType(selectedTable);
	// request AND inquiry both take the request CTA: an inquiry is a request to the guest.
	const simpleCta = requiresRequestFlow({ bookingType: effectiveBookingType })
		? <span>{t('actions.request_x', { x: tableLabel, defaultValue: `Request ${tableLabel}` })}</span>
		: <span className="!inline">
			{t('actions.select_x', { x: tableLabel, defaultValue: `Select ${tableLabel}` })}{shouldShowPrice ? <p className="inline whitespace-nowrap">{displayPrice()}</p> : null}
		</span>;


	return (
		<>
			{selectedTable?.isPatio && <PatioInfoSection />}

			<div className="w-full flex flex-col justify-center items-center gap-y-6">
				<BookNowButton
					disabled={disabled}
					tableHasAnnouncement={tableHasAnnouncement}
					announcementType={announcementType}
					ctaType="simplified"
					priceShown={priceAmountTotaled}
					priceCurrency={displayUnit}
					loading={!!isMutating}
					textNode={CTAOverride ? <span>{CTAOverride}</span> : simpleCta}
				/>
			</div>
		</>
	);
};